import React, { FC, useCallback, useEffect, useState } from 'react'

import { Button, Col, Divider, Form, Input, PageHeader, Radio, Row } from 'antd'
import { MaskedInput } from 'antd-mask-input'
import { SelectFieldAnt } from 'Components/Form'
import ID_COMPONENTE from 'config/idComponente'
import cpf from 'Hooks/useMask/cpf'
import { Motorista } from 'Models/cadastros'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import WsCadastroService from 'Services/WS_CADASTRO'
import { MsgSuccessOptions } from 'Types/SnackbarTypes'

interface IProps {
  mode: string
}

const FormMotoristas: FC<IProps> = ({ mode }: IProps) => {
  const hookForm = useForm()
  const navigate = useNavigate()
  const [openSnackbarSuccess, closeSnackbarSuccess] =
    useSnackbar(MsgSuccessOptions)
  const [itemData, setItemData] = useState<Motorista>()
  const [carregandoInclusaoOuEdicao, setCarregandoInclusaoOuEdicao] =
    useState<boolean>(false)
  const { id } = useParams()
  const [formulario] = Form.useForm<Motorista>()

  const getItemData = useCallback(async (idMotorista) => {
    const { data } = await WsCadastroService.motoristas.details(
      ID_COMPONENTE.DOCAS,
      idMotorista,
    )
    setItemData(data)
  }, [])

  useEffect(() => {
    if (id) {
      getItemData(parseInt(id))
    }
  }, [id, getItemData])

  const cadastraOuEditaMotorista = async (motorista: Motorista) => {
    setCarregandoInclusaoOuEdicao(true)

    if (motorista.cpf) {
      motorista.cpf = cpf.clean(motorista.cpf).toString()
    }

    if (motorista.telefone) {
      motorista.telefone = cpf.clean(motorista.telefone).toString()
    }

    if (motorista.ativo === undefined) {
      motorista.ativo = true
    }

    const { data } =
      mode === 'add'
        ? await WsCadastroService.motoristas.create(
            ID_COMPONENTE.DOCAS,
            motorista,
          )
        : await WsCadastroService.motoristas.update(
            ID_COMPONENTE.DOCAS,
            motorista,
            id,
          )

    if (data) {
      if (data.mensagem) {
        openSnackbarSuccess(data.mensagem)
      }

      if (data.statusCode === 200 || data.statusCode === 201) {
        navigate(-1)
      } else {
        setCarregandoInclusaoOuEdicao(false)
      }
    }
  }

  useEffect(() => {
    if (itemData) {
      formulario.setFieldsValue(itemData)
    }
  }, [itemData])

  return (
    <Form
      onFinish={cadastraOuEditaMotorista}
      layout="vertical"
      form={formulario}
    >
      <PageHeader
        ghost={false}
        title={mode === 'add' ? 'Incluir Motorista' : 'Editar Motorista'}
        extra={[
          <Button
            key="insercaoOuEdicao"
            type="primary"
            htmlType="submit"
            loading={carregandoInclusaoOuEdicao}
          >
            {mode === 'add' ? 'Incluir' : 'Salvar'}
          </Button>,

          <Button key="voltar" type="primary" onClick={() => navigate(-1)}>
            Voltar
          </Button>,
        ]}
      >
        <Row gutter={16}>
          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="nome" label="Nome:">
              <Input type="text" />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="sobrenome" label="Sobrenome:">
              <Input type="text" />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="codigoMotorista" label="Código:">
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="cpf" label="CPF:">
              <MaskedInput mask="111.111.111-11" />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item
              name="carteiraIdentidade"
              label="Carteira de Identidade (RG):"
            >
              <Input type="number" />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <SelectFieldAnt
              methods={hookForm}
              scope="form-motoristas"
              label="UF da Carteira de Identidade:"
              name="carteiraIdentidadeUf"
              items={[
                { value: 'AC', label: 'Acre (AC)' },
                { value: 'AL', label: 'Alagoas (AL)' },
                { value: 'AM', label: 'Amazonas (AM)' },
                { value: 'AP', label: 'Amapá (AP)' },
                { value: 'BA', label: 'Bahia (BA)' },
                { value: 'CE', label: 'Ceará (CE)' },
                { value: 'DF', label: 'Distrito Federal (DF)' },
                { value: 'ES', label: 'Espírito Santo (ES)' },
                { value: 'GO', label: 'Goiás (GO)' },
                { value: 'MA', label: 'Maranhão (MA)' },
                { value: 'MG', label: 'Minas Gerais (MG)' },
                { value: 'MT', label: 'Mato Grosso (MT)' },
                { value: 'MS', label: 'Mato Grosso do Sul (MS)' },
                { value: 'PA', label: 'Pará (PA)' },
                { value: 'PB', label: 'Paraíba (PB)' },
                { value: 'PR', label: 'Paraná (PR)' },
                { value: 'PE', label: 'Pernambuco (PE)' },
                { value: 'PI', label: 'Piauí (PI)' },
                { value: 'RJ', label: 'Rio de Janeiro (RJ)' },
                { value: 'RN', label: 'Rio Grande do Norte (RN)' },
                { value: 'RS', label: 'Rio Grande do Sul (RS)' },
                { value: 'RO', label: 'Rondônia (RO)' },
                { value: 'RR', label: 'Roraima (RR)' },
                { value: 'SC', label: 'Santa Catarina (SC)' },
                { value: 'SE', label: 'Sergipe (SE)' },
                { value: 'SP', label: 'São Paulo (SP)' },
                { value: 'TO', label: 'Tocantins (TO)' },
              ]}
              showSearch={true}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="dataNascimento" label="Data de Nascimento:">
              <MaskedInput mask="11/11/1111" />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="nomeMae" label="Nome da Mãe:">
              <Input type="text" />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="nomePai" label="Nome do Pai:">
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="ativo" label="Situação:">
              <Radio.Group defaultValue={true}>
                <Radio value={true}>Ativo</Radio>
                <Radio value={false}>Inativo</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Divider>Aparelhos Móveis</Divider>

        <Row gutter={16}>
          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="ddd" label="DDD:">
              <Input type="number" maxLength={2} />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="telefone" label="Telefone:">
              <Input type="number" maxLength={9} />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="nextel" label="Rádio:">
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>

        <Divider>Carteira de Habilitação (CNH)</Divider>

        <Row gutter={16}>
          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="numeroHabilitacao" label="Número:">
              <Input type="number" />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="validadeHabilitacao" label="Validade:">
              <MaskedInput mask="11/11/1111" />
            </Form.Item>
          </Col>

          <Col span={8} md={8} sm={12} xs={24}>
            <SelectFieldAnt
              methods={hookForm}
              scope="form-motoristas"
              label="Tipo:"
              name="tipoHabilitacao"
              items={[
                { value: 'A', label: 'A' },
                { value: 'AB', label: 'AB' },
                { value: 'B', label: 'B' },
                { value: 'C', label: 'C' },
                { value: 'D', label: 'D' },
                { value: 'E', label: 'E' },
              ]}
              showSearch={true}
            />
          </Col>
        </Row>

        <Divider>Telemetria</Divider>

        <Row gutter={16}>
          <Col span={8} md={8} sm={12} xs={24}>
            <Form.Item name="rfid" label="RFID:">
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
      </PageHeader>
    </Form>
  )
}

export default FormMotoristas
